/**
 * The role vocabulary, narrowed once at the API boundary.
 *
 * **Every read schema types `role` as a bare `string`** — `MembershipResponse`
 * and `InvitationResponse`, both of them — while the WRITE schemas
 * (`InvitationCreate`, `MembershipUpdate`) pin `^(viewer|member|admin|owner)$`.
 * So the contract validates a role on the way in and declines to state it on
 * the way out. Issue 306 asks the backend to reference its own enums; until it
 * does, this file is the client's stand-in, transcribed from the backend's
 * `api/src/models/household.py` `HouseholdRole`, whose own docstring gives the
 * hierarchy as viewer < member < admin < owner.
 *
 * **An unknown value is never defaulted.** `narrowRole` returns `null`, the
 * predicates below grant nothing to `null`, and the UI renders the raw string
 * with its actions disabled. Coercing an unrecognised role to `member` would
 * hand someone permissions the server never granted — a permission decision
 * that fails OPEN, which is the worst direction for one to fail in.
 */
export const ROLES = ['owner', 'admin', 'member', 'viewer'] as const

export type Role = (typeof ROLES)[number]

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
}

/**
 * The display label for a member — falling back to the email when
 * `displayName` is null (`user_display_name` is nullable on
 * `MembershipResponse`), and to a final fallback when the server has
 * neither. Shared by `MemberRow` (the row's own label) and `Household.tsx`
 * (the toast a confirmed remove raises, issue 366) — a second inline copy of
 * the same fallback chain at the second call site would drift from this one
 * the moment either changes.
 *
 * **A structural param, not `Pick<Member, ...>`** — matching `isLastOwner`
 * and `leaveBlockReason` below rather than importing `Member` from
 * `useMembers.ts`. That module reaches `api/client`, which throws at import
 * without `VITE_API_BASE`; `roles.ts` has no such dependency today. This
 * function first lived there, as a `Pick<Member, ...>`-typed helper — moved
 * here after `MemberRow.remove.test.tsx`/`MemberRow.role.test.tsx`/
 * `MemberRow.test.tsx` reported that error: none of the three mocks
 * `./useMembers`, and they were passing only because `MemberRow.tsx`'s own
 * import of `Member` was type-only and erased at build time. A VALUE import
 * of a helper living in that module would have made every one of them
 * evaluate the real thing.
 */
export function memberLabel(member: { displayName: string | null; email: string | null }): string {
  return member.displayName ?? member.email ?? 'Unnamed member'
}

/**
 * The three a household may hand out.
 *
 * **`owner` is absent deliberately.** It is acquired only by creating a
 * household, and iOS refuses to send it — its invite use case validates
 * against exactly this list. The server would likely accept it; the product
 * says being an owner is not something you can be invited into.
 */
export const INVITABLE_ROLES: readonly Role[] = ['admin', 'member', 'viewer']

/** Narrowing helper for values that arrive as strings — mirrors
 *  `isEntitlement` in `src/platform/access/entitlements.ts`. */
export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value)
}

/** `null` for anything outside the enum — never a fallback member. */
export function narrowRole(value: string): Role | null {
  return isRole(value) ? value : null
}

/**
 * Mirrors iOS `MembershipRole.canManageMembers` — invite, resend, revoke,
 * list invitations, and remove a member. Backed by `invitation_service.py:73,
 * 391, 450, 520` and `household_service.py:424`, all `can_perform(ADMIN)`.
 *
 * **Deliberately does not cover changing a member's role.** iOS has the same
 * gap (`MembershipDTO.swift:67`, also `owner || admin`), so mirroring iOS is
 * not authority here — the server is, and `household_service.py:363` guards
 * a role change with "owner only", 403ing an admin this predicate would
 * admit. Use `canChangeRole` for that action.
 */
export function canManageMembers(role: Role | null): boolean {
  return role === 'owner' || role === 'admin'
}

/**
 * Whether `role` may change another member's role.
 *
 * Restated from `household_service.py:363`: `role != OWNER` → 403 "Only
 * household owners can change member roles". Owner only — unlike every
 * other member-management action, an admin does not qualify.
 */
export function canChangeRole(role: Role | null): boolean {
  return role === 'owner'
}

/**
 * Whether `candidateUserId` is the viewer's own user id — expressing
 * `household_service.py:374`'s `target_user_id == user.id` → 400 "Cannot
 * change your own role". Nobody, not even an owner, may change their own
 * role.
 *
 * `myUserId` is `undefined` while the viewer's identity is still loading;
 * that must answer `false`, not `true` — an unknown viewer is not "self",
 * and answering `true` would wrongly suppress the role-change control for
 * everyone until the viewer resolves.
 *
 * **The `myUserId !== undefined` clause does not produce that `false` —
 * `candidateUserId === myUserId` already does, on its own.** `candidateUserId`
 * is typed `string`, so `string === undefined` is `false` for every value of
 * `candidateUserId`; the clause is redundant with the type, not load-bearing.
 * It stays because it makes that intent legible at the call site rather than
 * leaving a reader to work out the `undefined` case from the strict-equality
 * types — not because removing it would change behaviour.
 */
export function isSelf(candidateUserId: string, myUserId: string | undefined): boolean {
  return myUserId !== undefined && candidateUserId === myUserId
}

/** Mirrors iOS `MembershipRole.canEditTransactions`. */
export function canEditTransactions(role: Role | null): boolean {
  return role === 'owner' || role === 'admin' || role === 'member'
}

/**
 * Whether `candidate` is the only owner left — the one guard the UI must
 * enforce on **three** actions: demote, remove, and leave.
 *
 * Restated from M12-R3, which said "last admin". With four roles that is
 * wrong: demoting the last admin is harmless while an owner remains, and the
 * backend agrees — it guards "Cannot demote the last owner", "Cannot remove
 * the last owner" and "Cannot leave as last owner", and says nothing at all
 * about admins.
 *
 * Takes `{ role: string }` rather than `{ role: Role }` so raw wire rows and
 * narrowed ones both fit without every call site converting first.
 *
 * **Narrowing every role with `narrowRole` is a consistency measure, not the
 * behavioural guard.** `narrowRole` has no case-folding, so
 * `narrowRole(s) === 'owner'` iff `s === 'owner'` for every string — a plain
 * `=== 'owner'` on the same data answers identically. Do not assume the
 * narrowing is what makes an unclassifiable role fail closed; it is not. The
 * `candidateRole === null` check below is the actual guard: without it, a
 * candidate whose role string doesn't classify — not a case-variant, which
 * the database cannot produce (the `household_role` column is a native
 * Postgres enum with `values_callable`, so it persists and rejects only the
 * lowercase enum values; see `api/src/models/household.py`), but a role this
 * client has not been taught yet, such as a fifth role added server-side
 * (`'co_owner'`, say) — would read as "not an owner" and the UI would enable
 * demoting, removing, or leaving as the household's actual (sole) owner —
 * the worst direction this function can fail in. `null` therefore answers
 * `true`: we cannot confirm the candidate is *not* the last owner, so we do
 * not proceed as if they aren't. Symmetrically, a *member* whose role does
 * not narrow is never credited as a backup owner in the count below —
 * undercounting can only make this function block more, never less, which
 * is the safe direction.
 *
 * **`<= 1`, not `=== 1`, and the difference is not cosmetic.** They agree on
 * every input a caller is expected to supply, and diverge on one nobody has
 * to prevent: a candidate whose role is `owner` but who is absent from the
 * `members` array passed alongside. `<=` answers `true` there and blocks the
 * action; `===` answers `false` and lets demote, remove and leave all
 * through for someone whose ownership the list cannot account for. Blocking
 * costs a disabled control with a stated reason; the other direction removes
 * the last owner from a household. The boundary test in `roles.test.ts` pins
 * this, because without it the two spellings are indistinguishable and the
 * simpler-looking one silently wins a code review.
 */
export function isLastOwner(
  members: readonly { role: string }[],
  candidate: { role: string },
): boolean {
  const candidateRole = narrowRole(candidate.role)
  if (candidateRole === null) return true
  if (candidateRole !== 'owner') return false
  return members.filter((m) => narrowRole(m.role) === 'owner').length <= 1
}

/**
 * The one honest thing to say when the viewer's own role cannot be
 * confirmed at all — `leaveBlockReason`'s own fail-closed branch below, AND
 * `Household.tsx`'s footer control for a DIFFERENT cause (the member list
 * itself failing to load, rather than a role string that fails to narrow).
 * Exported rather than left as a literal inside `leaveBlockReason` so the
 * two call sites say the identical sentence rather than two that happen to
 * agree today — web-architect review, issue 314 fix round, B1: the loading
 * window used to borrow this same text for a request that simply hadn't
 * answered yet, which is not the same fact as either of the two cases this
 * string is actually true for.
 */
export const ROLE_UNCONFIRMED_LEAVE_REASON =
  'Your role could not be confirmed, so leaving is blocked here.'

/**
 * The loading window's own reason — a request that has not answered yet.
 *
 * The sibling above records that this window "used to borrow this same text",
 * which was wrong because a pending request is not a failed one. The fix then
 * was to give the loading window NO reason at all, and that turned out to be
 * wrong in the other direction: it left a blocked control with nothing naming
 * why, which is precisely what rule 11 and `DisabledReason.tsx` exist to
 * prevent, and `Household.tsx`'s footer wore it on every single load of
 * `/household`.
 *
 * `household-blocked-control.spec.ts:203` is what found it, intermittently —
 * it asserts every blocked control names its reason, and it won or lost that
 * race depending on how fast the members query settled. The spec was right
 * and the component was wrong.
 *
 * Distinct text, not a third borrowing: this one is temporary and resolves on
 * its own, which is the one thing the other two never do.
 */
export const ROLE_PENDING_LEAVE_REASON =
  'Checking your role in this household — leaving will be available in a moment.'

/**
 * Why the signed-in user cannot leave the household right now, or `null`
 * when they can — Task 7 (tracke5-webapp#314).
 *
 * **Two reasons, not one, even though `isLastOwner` alone answers the
 * BOOLEAN correctly for both.** `isLastOwner`'s own fail-closed branch
 * (`candidateRole === null` → `true`) is right about "block it", and wrong
 * as a SENTENCE: telling someone "you're the household's last owner" when
 * their role could not be classified at all is a claim this client cannot
 * support. Task 6's per-row guard sidesteps this by checking `member.role
 * !== null` before a control is even offered on someone ELSE'S row — Task 7
 * has no such check, because it acts on the viewer's OWN row, which is
 * exactly why the honest question has to be asked first, here.
 *
 * `myRawRole` is `undefined` both while the members list or the signed-in
 * identity has not resolved yet, and if the viewer's own row is (for some
 * reason) absent from `members` — both fail exactly like an unclassified
 * role, for the same reason `isLastOwner`'s own docblock gives: an
 * unresolved permission must not be read as "you may proceed".
 *
 * **Callers whose OWN reason for not having `myRawRole` yet is "still
 * loading" (`Household.tsx`'s footer, while `membersState.status !==
 * 'success'`) must NOT call this with `undefined` and take the answer as
 * their loading copy** — see `ROLE_UNCONFIRMED_LEAVE_REASON`'s own docblock.
 * This function has no way to tell "the list resolved and the viewer's row
 * is genuinely missing" from "the list hasn't resolved yet"; only the
 * caller knows which one it is.
 *
 * One function, called from both `Household.tsx`'s footer control and
 * `MemberActionsMenu`'s own Leave item, so the two entry points can never
 * disagree about whether — or why — leaving is blocked right now.
 */
export function leaveBlockReason(
  myRawRole: string | undefined,
  members: readonly { rawRole: string }[],
): string | null {
  if (myRawRole === undefined || narrowRole(myRawRole) === null) {
    return ROLE_UNCONFIRMED_LEAVE_REASON
  }
  if (isLastOwner(members.map((m) => ({ role: m.rawRole })), { role: myRawRole })) {
    return "You're the household's last owner — make someone else an owner first."
  }
  return null
}
